import { jsPDF } from "jspdf"
import "resource/font/THSarabun-normal"
import "resource/font/THSarabun-bold"
import dayjs from "dayjs"
import { printHeader } from "./pirntHeader"

// Loads a (possibly cross-origin) image URL and returns it as a JPEG data URL
// together with its natural dimensions, so jsPDF can embed it. Resolves null on
// any failure (missing file / CORS) so the report still prints without it.
const loadImage = (url) => new Promise((resolve) => {
  if (!url) return resolve(null)
  const img = new Image()
  img.crossOrigin = "anonymous"
  img.onload = () => {
    try {
      const canvas = document.createElement("canvas")
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      canvas.getContext("2d").drawImage(img, 0, 0)
      resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.85), w: img.naturalWidth, h: img.naturalHeight })
    } catch (e) {
      resolve(null)
    }
  }
  img.onerror = () => resolve(null)
  img.src = url
})

// The four device photos shown on the report, one of each type.
const IMAGE_TYPES = [
  { typ: "A", label: "แอลกอฮอล์ (Alcohol)" },
  { typ: "T", label: "อุณหภูมิ (Temperature)" },
  { typ: "B", label: "ความดันโลหิต (Blood Pressure)" },
  { typ: "U", label: "เครื่องแบบ (Uniform)" },
]

export const printDriverAttendCheckReport = async ({ reportData, companyData }) => {
  if (!reportData) return

  const files = reportData.Files || []
  // Pick the first file of each type and load all images in parallel.
  const pickedImages = await Promise.all(
    IMAGE_TYPES.map(async ({ typ, label }) => {
      const file = files.find(f => f.FileTyp === typ)
      const img = file ? await loadImage(file.Url) : null
      return { label, img }
    })
  )
  // Only photos that actually loaded are rendered; a missing one is skipped
  // entirely (no label, no border) instead of an empty placeholder box.
  const shownImages = pickedImages.filter(p => p.img)

  const pageSize = [8.3, 11.7] //A4
  const startX = 0.5
  const startY = 0.4
  const lineHeight = 0.25
  const contentSize = 14

  const doc = new jsPDF({ orientation: "portrait", unit: "in", format: pageSize })
  doc.setLineWidth(0.01)

  // ---------------- company header ----------------
  let yPos = printHeader({ jsPDFDoc: doc, companyData, startX, startY })
  yPos += lineHeight * 2

  // ---------------- title ----------------
  const pageWidth = pageSize[0]
  doc.setFont("THSarabun", "bold")
  doc.setFontSize(20)
  doc.text("รายงานความพร้อมก่อนปฏิบัติงาน", pageWidth / 2, yPos, { align: "center" })
  yPos += lineHeight * 1.5

  // ---------------- detail ----------------
  const chkInTm = reportData.DrvChkInTm ? dayjs(reportData.DrvChkInTm).format("DD/MM/YYYY HH:mm") : "-"
  const fullName = `${reportData.FName || ""} ${reportData.LName || ""}`.trim() || reportData.FLNNm || "-"
  const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v)

  const details = [
    { label: "ชื่อ-นามสกุล", value: fullName },
    { label: "เวลาเช็คอิน", value: chkInTm },
    { label: "ค่าแอลกอฮอล์ (Alcohol)", value: dash(reportData.Alc) },
    { label: "อุณหภูมิ (Temperature)", value: dash(reportData.Temp) },
    { label: "ความดันโลหิต (Blood Pressure)", value: dash(reportData.BP) },
  ]

  const labelCol = startX
  const valueCol = startX + 2.2
  doc.setFontSize(contentSize)
  for (const d of details) {
    doc.setFont("THSarabun", "bold")
    doc.text(`${d.label} :`, labelCol, yPos)
    doc.setFont("THSarabun", "normal")
    doc.text(`${d.value}`, valueCol, yPos)
    yPos += lineHeight
  }
  yPos += lineHeight / 2

  // ---------------- images (2 x 2 grid) ----------------
  const gap = 0.3
  const contentWidth = pageWidth - startX * 2
  const colWidth = (contentWidth - gap) / 2
  // Portrait image box, 3:2 vertical (width:height = 2:3), centered in each column cell.
  const imgBoxWidth = 2.2
  const imgBoxHeight = imgBoxWidth * 3 / 2

  for (let i = 0; i < shownImages.length; i++) {
    const col = i % 2
    if (col === 0 && i > 0) {
      yPos += lineHeight + imgBoxHeight + lineHeight
    }
    const cellX = startX + col * (colWidth + gap)
    const boxX = cellX + (colWidth - imgBoxWidth) / 2
    const { label, img } = shownImages[i]

    // label
    doc.setFont("THSarabun", "bold")
    doc.setFontSize(contentSize)
    doc.text(label, cellX + colWidth / 2, yPos, { align: "center" })

    // image box
    const boxY = yPos + lineHeight / 2
    doc.rect(boxX, boxY, imgBoxWidth, imgBoxHeight)
    const scale = Math.min((imgBoxWidth - 0.1) / img.w, (imgBoxHeight - 0.1) / img.h)
    const drawW = img.w * scale
    const drawH = img.h * scale
    const imgX = boxX + (imgBoxWidth - drawW) / 2
    const imgY = boxY + (imgBoxHeight - drawH) / 2
    doc.addImage(img.dataUrl, "JPEG", imgX, imgY, drawW, drawH)
  }

  // ---------------- footer note ----------------
  const pageHeight = pageSize[1]
  const footerText = "หมายเหตุ: รูปภาพและเวลาที่แสดงในเอกสารนี้ถูกบันทึกและพิมพ์จากระบบ Emily พนักงานขับรถไม่สามารถนำรูปภาพเดิมมาใช้ซ้ำหรือแก้ไขได้"
  doc.setFont("THSarabun", "normal")
  doc.setFontSize(12)
  doc.setTextColor(120)
  const footerLineHeight = lineHeight - 0.03
  const footerLines = doc.splitTextToSize(footerText, contentWidth)
  let footerY = pageHeight - 0.4 - (footerLines.length - 1) * footerLineHeight
  for (const line of footerLines) {
    doc.text(line, pageWidth / 2, footerY, { align: "center" })
    footerY += footerLineHeight
  }
  doc.setTextColor(0)

  const blob = new Blob([doc.output("blob")], { type: "application/pdf" })
  const blobUrl = URL.createObjectURL(blob)
  window.open(blobUrl, "_blank")
}
